import React, { useCallback, useRef, useState } from "react"
import styled from "styled-components"
import Text from "@/components/common/Text"
import CommonButton from "@/components/common/Buttons/Button"
import { getCssSizeValue, getHtmlFromRedactorField } from "@/utils/utils"
import { BREAKPOINTS } from "@/styles/themeSettings"
import useColor from "@/hooks/useColor"
import GridContainer from "@/components/common/GridContainer"
import { gsap, useGSAP } from "@/utils/gsap"
import useScrollContext from "@/hooks/useScrollContext"
import { BUTTON_TYPE, CMS_ELEMENT_TYPENAME, CONTENT_TYPES } from "@/constants"
import { useIsDesktop } from "@/hooks/useDevice"

const Wrapper = styled(GridContainer)`
	background-color: ${({ $bgColor }) => $bgColor || "transparent"};
	transition: background-color var(--rgr-duration) var(--ease);
	row-gap: unset;
	cursor: pointer;
	border-top: solid 1px ${({ theme }) => theme.colors.slateGray};

	&.last {
		border-bottom: solid 1px ${({ theme }) => theme.colors.slateGray};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		min-height: ${getCssSizeValue(198)};
	}

	&:hover {
		background-color: ${({ theme }) => theme.colors.platinum};
	}
`

const Row = styled.div`
	display: flex;
	flex-direction: column;
	grid-column: 1/9;

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
		grid-column: 2/9;
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		grid-column: 2/16;
	}

	&.top {
		min-height: ${getCssSizeValue(160, BREAKPOINTS.MOBILE)};
		padding: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)} 0 ${getCssSizeValue(56, BREAKPOINTS.MOBILE)};

		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
			min-height: ${getCssSizeValue(160, BREAKPOINTS.TABLET)};
			grid-column-start: 1;
			padding: ${getCssSizeValue(24, BREAKPOINTS.TABLET)} 0;
		}

		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			min-height: ${getCssSizeValue(220, BREAKPOINTS.DESKTOP)};
			grid-column-start: 2;
			padding: ${getCssSizeValue(32)} 0;
		}

		.text {
			grid-column-start: 1;
		}

		.header {
			margin-top: auto;
		}

		&.expanded {
			border-bottom: solid 1px ${({ theme }) => theme.colors.slateGray};
		}

		.text-and-button {
			display: flex;
			justify-content: space-between;
		}
	}

	&.bottom {
		display: grid;
		grid-template-columns: repeat(8, 1fr);

		transition: opacity var(--rgr-duration) var(--ease);
		height: 100%;
		opacity: 0;
		overflow: hidden;
		pointer-events: none;
		max-height: 0;

		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			grid-template-columns: repeat(14, 1fr);
			gap: var(--grid-gap);
		}

		> .text {
			padding: ${getCssSizeValue(40, BREAKPOINTS.MOBILE)} 0;

			@media (max-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP] - 1}px) {
				&:not(&:first-of-type) {
					padding-top: unset;
				}
			}

			@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
				padding: ${getCssSizeValue(40, BREAKPOINTS.TABLET)} 0;
			}

			@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
				padding: ${getCssSizeValue(40)} 0;
			}
		}

		&.expanded {
			opacity: 1;
			pointer-events: auto;
		}
	}
`

const Button = styled(CommonButton)`
	--color: unset;
	--bg-color: unset;
	--stick-size: 2px;
	--size: ${getCssSizeValue(42, BREAKPOINTS.MOBILE)};
	margin-left: auto;
	position: relative;
	width: var(--size);
	min-height: var(--size);
	transition: background-color var(--rgr-duration) var(--ease);
	background-color: var(--bg-color) !important;

	&:before,
	&:after {
		content: "";
		position: absolute;
		left: 50%;
		top: 50%;
		translate: -50% -50%;
		background-color: var(--color);
		transition: background-color var(--rgr-duration) var(--ease);
	}

	&:before {
		width: var(--stick-size);
		height: 50%;
		transition: rotate var(--rgr-duration) var(--ease);
	}

	&:after {
		width: 50%;
		height: var(--stick-size);
	}

	&.expand {
		--color: ${({ theme }) => theme.colors.white};
		--bg-color: ${({ theme }) => theme.colors.chilliRed};
	}

	&.hide {
		--color: ${({ theme }) => theme.colors.chilliRed};
		--bg-color: ${({ theme }) => theme.colors.white};

		&:before {
			rotate: -90deg;
		}
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
		--size: ${getCssSizeValue(50, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		--size: ${getCssSizeValue(50)};
	}
`

function getChildrenSummarizedHeight(target) {
	if (!target) return
	return [...target.children].reduce((acc, curr) => acc + curr.getBoundingClientRect()?.height || 0, 0)
}

const Tab = ({ data, className = "", count = 1, isLast = false, ...props }) => {
	const WrapperRef = useRef()
	const header = data?.header || ""
	const description = data?.description || ""
	const description2 = data?.description2 || ""
	const hdTypename = data?.[CMS_ELEMENT_TYPENAME] || CONTENT_TYPES.HEADER_AND_DESCRIPTION_TWO_COLUMN_BLOCK
	const redactor = getHtmlFromRedactorField(description)
	const redactor2 = getHtmlFromRedactorField(description2)
	const BottomRef = useRef()
	const scroll = useScrollContext()
	const [active, setActive] = useState(false)
	const bgColor = useColor(!active ? "white" : "platinum")
	const handleOnClick = useCallback(() => setActive((prev) => !prev), [])
	const isDesktop = useIsDesktop()

	useGSAP(
		() => {
			let targetHeight = 0
			if (active) {
				if (!isDesktop) {
					targetHeight = getChildrenSummarizedHeight(BottomRef.current)
				} else {
					// targetHeight = getBiggestHeightOfElementChildren(BottomRef.current)
					targetHeight = getChildrenSummarizedHeight(BottomRef.current)
				}
			}
			gsap.to(BottomRef.current, {
				maxHeight: `${targetHeight}px`,
				onComplete: () => {
					scroll.refresh()
				},
			})
		},
		{
			dependencies: [active, isDesktop],
			scope: BottomRef.current,
		},
	)

	return (
		<Wrapper
			ref={WrapperRef}
			className={`${className} ${isLast ? "last" : ""} tab no-margin padding`}
			$bgColor={bgColor}
			aria-expanded={active}
			onClick={handleOnClick}
			{...props}
		>
			<Row className={`top col-mobile-8 col-tablet-8 col-desktop-16 ${active ? "expanded" : ""}`}>
				<div className="text-and-button">
					<Text className="typo-borna-30">0{count}</Text>
					<Button
						type={BUTTON_TYPE.UNSTYLED}
						// onClick={handleOnClick}
						className={`${active ? "hide" : "expand"}`}
						aria-label="click to expand"
						animation={false}
						noHoverEffect
					/>
				</div>
				<Text className="typo-borna-28 header">{header}</Text>
			</Row>
			<Row ref={BottomRef} className={`${active ? "expanded" : ""} bottom`}>
				{hdTypename === CONTENT_TYPES.HEADER_AND_DESCRIPTION_TWO_COLUMN_BLOCK ? (
					<>
						<Text
							as="div"
							className="typo-strawford-67 col-mobile-8 col-tablet-7 col-desktop-7"
							dangerouslySetInnerHTML={redactor}
						/>
						<Text
							as="div"
							className="typo-strawford-67 col-mobile-8 col-tablet-7 col-desktop-7"
							dangerouslySetInnerHTML={redactor2}
						/>
					</>
				) : (
					<Text
						as="div"
						className="typo-strawford-67 col-mobile-8 col-tablet-8 col-desktop-16"
						dangerouslySetInnerHTML={redactor}
					/>
				)}
			</Row>
		</Wrapper>
	)
}

export default Tab
